import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const CUSTOM = "Custom Amount";
const tdsRate = (t) => {
  const s = (t || "").toLowerCase();
  if (!s.includes("gst")) return 0;
  return s.includes("non") ? 1 : 2;
};

export const pctAmount = (pct, total) => Math.round((parseFloat(pct) || 0) * Number(total || 0)) / 100;
export const customInvalidFor = (pct, total, remaining) => {
  const p = parseFloat(pct) || 0;
  return p <= 0 || p > 100 || pctAmount(pct, total) > Number(remaining || 0) + 0.001;
};

export function CustomAmountInput({ value, onChange, total, remaining, invoiceType }) {
  const pct = parseFloat(value) || 0;
  const amt = pctAmount(value, total);
  const rate = tdsRate(invoiceType);
  const tds = Math.round(amt * rate) / 100;
  const maxPct = total > 0 ? Math.floor((remaining / total) * 10000) / 100 : 0;
  const over = amt > remaining + 0.001 || pct > 100;
  return (
    <div className="space-y-2 p-3 border border-blue-200 rounded-md bg-white" data-testid="custom-amount-box">
      <label className="text-xs uppercase tracking-wider text-slate-500">Percentage of total {fmtCur(total)} · max {maxPct}%</label>
      <div className="relative">
        <input
          type="number" min="0" max="100" step="0.01" inputMode="decimal" value={value} autoFocus
          onChange={(e) => onChange(e.target.value)}
          className={`w-full border rounded-md pl-3 pr-8 py-2 text-sm font-mono ${over ? "border-rose-400" : "border-slate-300"}`}
          data-testid="custom-amount-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">%</span>
      </div>
      {over && <div className="text-[11px] text-rose-600" data-testid="custom-amount-error">Can't exceed {maxPct}% (the remaining unpaid balance of {fmtCur(remaining)}).</div>}
      {value !== "" && pct <= 0 && <div className="text-[11px] text-rose-600" data-testid="custom-amount-error">Enter a percentage greater than 0.</div>}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="p-2 rounded bg-blue-50 border border-blue-100">
          <div className="text-[10px] uppercase tracking-wider text-blue-700">Amount ({pct || 0}%)</div>
          <div className="font-mono font-semibold text-blue-900" data-testid="custom-amount-gross">{fmtCur(amt)}</div>
        </div>
        <div className="p-2 rounded bg-rose-50 border border-rose-100">
          <div className="text-[10px] uppercase tracking-wider text-rose-700">TDS ({rate}% · {invoiceType || "—"})</div>
          <div className="font-mono font-semibold text-rose-800" data-testid="custom-amount-tds">− {fmtCur(tds)}</div>
        </div>
        <div className="p-2 rounded bg-emerald-50 border border-emerald-100">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Net amount</div>
          <div className="font-mono font-semibold text-emerald-800" data-testid="custom-amount-net">{fmtCur(amt - tds)}</div>
        </div>
      </div>
    </div>
  );
}

export default function RequestPaymentDialog({ open, onOpenChange, creator, onCreated }) {
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [note, setNote] = useState("");
  const [customAmt, setCustomAmt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !creator?.id) return;
    setSelected(""); setNote(""); setCustomAmt(""); setElig(null); setLoading(true);
    api.get(`/approvals/eligibility`, { params: { creator_id: creator.id } })
      .then(({ data }) => { setElig(data); setSelected((data.available || [])[0] || ""); })
      .catch(() => setElig({ available: [], suggested_amount: {}, has_pending: false }))
      .finally(() => setLoading(false));
  }, [open, creator?.id]);

  const submit = async () => {
    if (!selected) return;
    // Guard rail: frontend-side invoice check mirrors the backend 400.
    if (elig && !elig.has_invoice) {
      toast.error("Invoice not available — please upload an invoice and try again.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/approvals", {
        creator_id: creator.id, payment_type: selected, note,
        ...(selected === CUSTOM ? { percent: parseFloat(customAmt) } : {}),
      });
      toast.success("Payment request raised");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to raise request");
    } finally { setSubmitting(false); }
  };

  const available = elig?.available || [];
  const amounts = elig?.suggested_amount || {};
  const invoiceMissing = elig && !elig.has_invoice;
  const remaining = Number(elig?.remaining || 0);
  const total = Number(elig?.final_cost || creator?.final_cost || 0);
  const customInvalid = selected === CUSTOM && customInvalidFor(customAmt, total, remaining);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="request-payment-dialog">
        <DialogHeader>
          <DialogTitle>Request payment</DialogTitle>
          <DialogDescription>
            @{creator?.username} · Final cost {fmtCur(creator?.final_cost)} · Paid {fmtCur(elig?.total_paid)}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : invoiceMissing ? (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="creator-no-invoice">
            Invoice not available — please upload an invoice and try again.
          </div>
        ) : elig?.has_pending ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            A pending request already exists for this creator. Wait for the admin to release or reject it before raising a new one.
          </div>
        ) : available.length === 0 ? (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="no-payment-options">
            Payment complete — nothing left to request.
          </div>
        ) : (
          <div className="space-y-3">
            {available.map((pt) => (
              <label key={pt} className={`flex items-center justify-between gap-3 p-3 border rounded-md cursor-pointer transition-colors ${selected === pt ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`payment-type-${pt.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="payment_type" checked={selected === pt} onChange={() => setSelected(pt)} className="accent-blue-600" />
                  <span className="text-sm font-medium text-slate-900">{pt}</span>
                </div>
                <span className="font-mono text-sm font-semibold text-slate-900">{pt === CUSTOM ? "Enter %" : fmtCur(amounts[pt] || 0)}</span>
              </label>
            ))}
            {selected === CUSTOM && (
              <CustomAmountInput value={customAmt} onChange={setCustomAmt} total={total} remaining={remaining} invoiceType={elig?.invoice_type || creator?.invoice_type} />
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                data-testid="request-note"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="request-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!selected || submitting || available.length === 0 || elig?.has_pending || invoiceMissing || customInvalid}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="request-submit"
          >
            {submitting ? "Submitting…" : "Request payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
